<template>
  <layout :loading="isLoading" :isTop="false">
    <v-row class="mt-3">
      <v-col cols="12" :lg="colProfile" class="pr-0">
        <div
          class="d-flex mb-3 align-xl-center flex-column flex-xl-row text-capitalize"
        >
          <div class="text-h4">{{ clientName }}</div>
          <v-divider vertical class="mx-3"></v-divider>
          <div>
            <div v-if="referralName">
              <b>Referred By</b> : {{ referralName }}
            </div>
            <div v-if="clientAffilate">
              <b>Affiliate</b> : {{ clientAffilate }}
            </div>
          </div>
        </div>
        <update-client-status class="mb-3" :item="client" />
        <div class="d-flex mb-3 align-center">
          <div class="d-flex" v-if="client.email">
            <v-icon class="mr-1">mdi-email</v-icon>
            {{ client.email }}
          </div>
          <div class="d-flex ml-3" v-if="client.mobile">
            <v-icon class="mr-1">mdi-phone</v-icon>
            {{ client.mobile }}
          </div>
        </div>
        <div class="d-flex mb-3 black--text text-capitalize">
          {{ address }}
          <span v-if="address" class="mx-1">|</span>
          <template v-if="formatDateOnly(client.birth_date) != 'Invalid date'">
            <tooltip :message="`DOB: ${formatDateOnly(client.birth_date)}`">
              <span @click="copyDOB(true)" class="cursor-pointer">
                DOB:&nbsp;</span
              >
            </tooltip>
            <tooltip :message="formatDateOnly(client.birth_date)">
              <a class="black--text" @click="copyDOB(false)">
                {{ formatDateOnly(client.birth_date) }}</a
              >
            </tooltip>
          </template>
          <span v-if="client.ss_number">
            <span class="mx-1"> | </span>
            <tooltip :message="`SSN: ${client.ss_number}`">
              <span @click="copyClipboard(true)" class="cursor-pointer">
                SSN:&nbsp;
              </span>
            </tooltip>
            <tooltip :message="client.ss_number">
              <a class="black--text" @click="copyClipboard(false)">
                XXX-XX-{{ ssn }}</a
              >
            </tooltip>
          </span>
        </div>
        <router-link
          class="text-decoration-none"
          v-if="hasEdit('customers') || isAffiliateAuth"
          :to="{ name: 'update-client', params: { id: clientId } }"
          >View/Edit Profile</router-link
        >
        <template v-if="hasEdit('client-agreements')">
          <span class="mx-3">|</span>
          <add-contract />
        </template>
        <template v-if="hasRead('client-agreements')">
          <span class="mx-3">|</span>
          <client-contract-history />
        </template>
      </v-col>
      <v-col cols="12" :lg="colAccounts" class="px-0">
        <v-slide-group
          ref="accountGroup"
          class="account-group"
          active-class="success"
          show-arrows
          @click:prev="snapAccountGroup"
          @click:next="snapAccountGroup"
        >
          <!-- v-slide-group scrolls to a focused card, so clicking copy moved
               the slider; focusin is stopped before it gets there -->
          <draggable
            v-model="accounts"
            style="display: flex"
            draggable=".account-item"
            @change="onPositionUpdate"
            @focusin.native.stop
          >
            <v-slide-item
              v-for="account in accounts"
              :key="account.prefix"
              :style="{ width: `${accountWidth}px` }"
            >
              <client-report-account
                :class="hasEdit('customers') ? 'account-item' : ''"
                :type="account.prefix"
                :account="account"
                :logo="account.logo"
              />
            </v-slide-item>
          </draggable>
        </v-slide-group>
      </v-col>
    </v-row>
    <v-divider class="mt-5"></v-divider>
    <v-row class="my-0">
      <v-col cols="12" lg="6" class="pb-10">
        <client-profile-scores />
        <client-profile-progress ref="progress" />
        <div class="d-flex mb-5" v-if="canChargeClient">
          <charge-client-dialog />
          <client-recurring-schedule />
          <client-profile-payment-methods />
        </div>
        <v-divider class="my-3" />
        <client-profile-dispute-status />
      </v-col>
      <v-col
        cols="12"
        lg="6"
        class="pb-10"
        style="border-left: 1px solid #0000001f"
      >
        <template v-if="!isAffiliateAuth">
          <client-team-tasks />
          <v-divider class="mb-3 mt-1" />
          <div class="title mb-3">Internal Memo</div>
          <div>
            <div
              v-if="!isMemoEdit"
              @click="editMemo()"
              class="pa-3 indigo lighten-5 rounded-lg"
              :class="hasEdit('customers') ? 'cursor-pointer' : ''"
              v-html="memoHTML"
            ></div>
            <template v-else>
              <textarea-field v-model="memo" :rows="10" />
              <div class="d-flex">
                <v-spacer></v-spacer>
                <v-btn
                  color="grey"
                  class="mr-3"
                  @click="isMemoEdit = false"
                  outlined
                  :disabled="isMemoLoading"
                  >Cancel</v-btn
                >
                <v-btn
                  color="success"
                  depressed
                  @click="updateMemo()"
                  :loading="isMemoLoading"
                  >Save Memo</v-btn
                >
              </div>
            </template>
          </div>
          <v-divider class="my-3" />
        </template>
        <client-affiliate-memo></client-affiliate-memo>
        <client-profile-documents
          @onFileUpdate="$refs.progress.fetchProgress()"
        />
      </v-col>
    </v-row>
    <v-divider></v-divider>
  </layout>
</template>

<script>
import Layout from "@/components/ui/Layout.vue";
import UpdateClientStatus from "@/components/clients/UpdateClientStatus.vue";
import Tooltip from "@/components/ui/Tooltip.vue";
import ClientProfileScores from "@/components/clients/clientProfile/ClientProfileScores.vue";
import ClientProfileProgress from "@/components/clients/clientProfile/ClientProfileProgress.vue";
import ClientProfileDocuments from "@/components/clients/clientProfile/ClientProfileDocuments.vue";
import ClientReportAccount from "./ClientReportAccount.vue";
import ClientProfileDisputeStatus from "@/components/clients/clientProfile/ClientProfileDisputeStatus.vue";
import TextareaField from "@/components/ui/form/TextareaField.vue";
import {doc, getDoc, updateDoc} from "firebase/firestore";
import {colClients, colReferrals} from "@/assets/utils/firebase.utils";
import {eventBus, getDate} from "@/assets/utils/common.utils";
import ClientTeamTasks from "@/components/clients/clientProfile/ClientTeamTasks.vue";
import ChargeClientDialog from "@/components/clients/clientProfile/ChargeClientDialog.vue";
import ClientRecurringSchedule from "@/components/clients/clientProfile/ClientRecurringSchedule.vue";
import draggable from "vuedraggable";
import ClientProfilePaymentMethods from "@/components/clients/clientProfile/ClientProfilePaymentMethods.vue";
import AddContract from "@/components/clients/clientProfile/AddContract.vue";
import ClientContractHistory from "@/components/clients/clientProfile/ClientContractHistory.vue";
import {mapGetters} from "vuex";
import ClientAffiliateMemo from "@/components/clients/clientProfile/ClientAffiliateMemo.vue";

export default {
  components: {
    ClientAffiliateMemo,
    Layout,
    UpdateClientStatus,
    Tooltip,
    ClientProfileScores,
    ClientProfileProgress,
    ClientProfileDocuments,
    ClientReportAccount,
    ClientProfileDisputeStatus,
    TextareaField,
    ClientTeamTasks,
    ChargeClientDialog,
    ClientRecurringSchedule,
    draggable,
    ClientProfilePaymentMethods,
    AddContract,
    ClientContractHistory,
  },
  data() {
    return {
      isLoading: false,
      isMemoEdit: false,
      isMemoLoading: false,
      memo: "",
      accountOrder: [],
      referralName: "",
      accountWidth: 240,
      accounts: [
        {
          prefix: "sc",
          logo: require("@/assets/images/smartcredit.svg"),
          link: "https://www.smartcredit.com/login/",
        },
        {
          prefix: "enfortra",
          logo: require("@/assets/images/enfortra.png"),
          link: "https://www.identityprotection-services.com",
        },
        {
          prefix: "equipfax",
          logo: require("@/assets/images/equifax.png"),
          class: "px-5 pt-4",
          link: "https://my.equifax.com/membercenter/#/login",
        },
        {
          prefix: "experian",
          logo: require("@/assets/images/experian.svg"),
          class: "py-1",
          link: "https://usa.experian.com/login/index?br=exp&op=XXXX-LOG-PRI-XXX-LGM-XXXXXXX-XX-EXP-VWIN-REF-XXXXXX-XXXXXX-XXXXX&_gl=1*1q95ye8*_gcl_au*Mzk1NzYyMjcxLjE3NTIwODA1MTQ.*_ga*MTkzMDI0MzU4NS4xNzE5NzgxMzY4*_ga_22YNWJN8Z2*czE3NTIyNDgwNjQkbzE0JGcxJHQxNzUyMjQ4MDY0JGo2MCRsMCRoMA..*_ga_7EN8BHB8L8*czE3NTIyNDgwNjQkbzYkZzEkdDE3NTIyNDgwNjQkajYwJGwwJGgw",
        },
        {
          prefix: "transunion",
          logo: require("@/assets/images/transunion.png"),
          class: "py-1",
          link: "https://service.transunion.com/dss/login.page?PLACE_CTA=TransUnion:DSS:Login",
        },
        {
          prefix: "creditkarma",
          class: "px-2",
          logo: require("@/assets/images/creditkarma.svg"),
          link: "https://www.creditkarma.com/auth/logon",
        },
      ],
    };
  },
  computed: {
    colProfile() {
      if (window.innerWidth >= 1920) return 5;
      return 6;
    },
    colAccounts() {
      if (window.innerWidth >= 1920) return 7;
      return 6;
    },
    clientName() {
      let { first_name, last_name, middle_name } = this.currentClient;
      let initial = (middle_name || "").toString().trim().charAt(0);
      if (initial) return `${first_name} ${initial} ${last_name}`;
      return `${first_name} ${last_name}`;
    },
    memoHTML() {
      let memo = this.currentClient.memo || "-";
      return memo.split("\n").join("<br/>");
    },
    client() {
      return { ...this.currentClient };
    },
    clientId() {
      return this.$route.params.id;
    },
    ssn() {
      let ssn = this.client.ss_number || "";
      return ssn.toString().substr(ssn.toString().length - 4);
    },
    address() {
      let { street, state, postal_code, city } = this.client;
      return `${street || ""} ${city || ""}, ${state || ""} ${
        postal_code || ""
      }`;
    },
    ...mapGetters(["userName"]),
    clientAffilate() {
      if (this.isAffiliateAuth) return this.userName;
      let { affiliate_id, affiliate_id_2, affiliate_id_3 } = this.client;
      let list = [];
      if (affiliate_id) list.push(this.getAffiliateName(affiliate_id));
      if (affiliate_id_2) list.push(this.getAffiliateName(affiliate_id_2));
      if (affiliate_id_3) list.push(this.getAffiliateName(affiliate_id_3));
      return list.toString().split(",").join(", ");
    },
  },
  methods: {
    onPositionUpdate() {
      let account_orders = this.accounts.map((i) => i.prefix);
      updateDoc(doc(colClients, this.$route.params.id), {
        account_orders,
      });
    },
    copyClipboard(withSSNText) {
      let ssn = this.client.ss_number;
      if (withSSNText) {
        ssn = `SSN: ${ssn}`;
      }
      navigator.clipboard.writeText(ssn);
      this.notifySuccess("SSN Copied");
    },
    copyDOB(withDOBText) {
      let dob = this.formatDateOnly(this.client.birth_date);
      if (withDOBText) {
        dob = `DOB: ${dob}`;
      }
      navigator.clipboard.writeText(dob);
      this.notifySuccess("DOB Copied");
    },
    editMemo() {
      if (this.hasEdit("customers")) {
        this.isMemoEdit = true;
        this.memo = this.currentClient.memo || "";
      }
    },
    async updateMemo() {
      try {
        this.isMemoLoading = true;
        await updateDoc(doc(colClients, this.$route.params.id), {
          memo: this.memo,
          updated_by: this.userEmail,
          updated_at: getDate(),
        });
        this.isMemoEdit = false;
        this.isMemoLoading = false;
      } catch (error) {
        this.isMemoLoading = false;
        this.handleError(error);
      }
    },
    initAccountOrder() {
      let orders = this.currentClient.account_orders || [];
      let orderFound = orders
        .map((i) => {
          let item = this.accounts.find((a) => a.prefix == i);
          return item;
        })
        .filter((i) => i != null);
      let orderNotFound = this.accounts.filter((i) => {
        return orders.find((o) => o == i.prefix) == null;
      });
      this.accounts = [...orderFound, ...orderNotFound];
    },
    // Size the cards so a whole number of them fills the slider; otherwise the
    // last one is cut and its copy buttons sit under the arrow.
    fitAccounts(wrapper) {
      if (!wrapper) return;
      const count = Math.max(1, Math.floor(wrapper / 220));
      this.accountWidth =
        count >= this.accounts.length
          ? Math.min(240, Math.floor(wrapper / this.accounts.length))
          : Math.floor(wrapper / count);
    },
    // Arrows scroll by the slider width; round it down to whole cards so the
    // left card is never cut. The last step still ends flush with the right.
    snapAccountGroup() {
      this.$nextTick(() => {
        const group = this.$refs.accountGroup;
        const max = group.widths.content - group.widths.wrapper;
        if (group.scrollOffset >= max) return;
        group.scrollOffset =
          Math.floor(group.scrollOffset / this.accountWidth) * this.accountWidth;
      });
    },
  },
  beforeDestroy() {
    eventBus.$off("refreshProfile");
  },
  beforeMount() {
    this.initAccountOrder();

    eventBus.$on("refreshProfile", () => {
      this.initAccountOrder();
    });
  },
  async mounted() {
    this.$watch(() => this.$refs.accountGroup.widths.wrapper, this.fitAccounts, {
      immediate: true,
    });
    if (this.client.referral_id) {
      const referral = await getDoc(doc(colReferrals, this.client.referral_id));
      const referralData = referral.data();
      if (referralData) {
        this.referralName = referralData.first_name + " " + referralData.last_name;
      }
    }
  }
};
</script>

<style lang="scss">
.account-group {
  .v-slide-group__next,
  .v-slide-group__prev {
    flex: 0 0 40px;
    min-width: 40px !important;
  }
  // Keep a cut-off card's copy buttons clear of the arrows.
  .v-slide-group__wrapper {
    margin: 0 8px;
  }
}
</style>
